import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { selectInitialLoad, selectPortableMode, selectUser } from '../../containers/App/selectors';
import Text from '../Text/Text';
import Flex from '../Flex/Flex';
import './SubscriptionExpiryWarning.css';

const EXPIRY_WARNING_DAYS = 5;
const warningStorageKey = (userId: number) =>
  `subscription-expiry-warning-${userId}-${dayjs().format('YYYY-MM-DD')}`;

const SubscriptionExpiryWarning = React.memo(() => {
  const { t, i18n } = useTranslation();
  const user = useSelector(selectUser);
  const portableMode = useSelector(selectPortableMode);
  const initialLoad = useSelector(selectInitialLoad);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!initialLoad || !user || portableMode) {
      setVisible(false);
      return;
    }

    const { subscription, subscriptionDate, id } = user;
    if (!subscription || !subscriptionDate) {
      setVisible(false);
      return;
    }

    const daysLeft = dayjs(subscriptionDate.slice(0, 10)).diff(dayjs().startOf('day'), 'day');

    if (daysLeft < 0 || daysLeft > EXPIRY_WARNING_DAYS) {
      setVisible(false);
      return;
    }

    if (localStorage.getItem(warningStorageKey(id)) === '1') {
      setVisible(false);
      return;
    }

    setVisible(true);
    localStorage.setItem(warningStorageKey(id), '1');
  }, [initialLoad, user, portableMode]);

  if (!visible || !user?.subscriptionDate) {
    return null;
  }

  const daysLeft = dayjs(user.subscriptionDate.slice(0, 10)).diff(dayjs().startOf('day'), 'day');
  const formattedDate = dayjs(user.subscriptionDate.slice(0, 10))
    .locale(i18n.language)
    .format('DD MMMM YYYY');

  return (
    <aside className="SurfaceNote SubscriptionExpiryWarning">
      <Flex align="flex-start" justify="space-between" gap={12}>
        <Flex vertical gap={8}>
          <Text size={3} bold>
            {daysLeft === 0
              ? t('subscription_expiry_warning_today', { date: formattedDate })
              : t('subscription_expiry_warning_soon', {
                  days: daysLeft,
                  date: formattedDate,
                })}
          </Text>
          <Text size={2}>
            <Link to="/subscription">{t('subscription')}</Link>
            {' · '}
            <a href="https://t.me/fishh_support" target="_blank" rel="noreferrer">
              https://t.me/fishh_support
            </a>
          </Text>
        </Flex>
        <button
          type="button"
          className="SubscriptionExpiryWarning__close"
          aria-label="Close"
          onClick={() => setVisible(false)}
        >
          ×
        </button>
      </Flex>
    </aside>
  );
});

export default SubscriptionExpiryWarning;
